import { useMemo, useState } from "react";
import { RefreshCw, X } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { BracketTable } from "@/components/deck/BracketTable";

// CP-007: review and publish one group's update (internal ranking + bracket, optional members list).
const STATS = ["participation", "attacks", "defenses", "repairs", "heals"];
const HEAD = { participation: "Participation", attacks: "Attacks", defenses: "Defenses", repairs: "Repairs", heals: "Heals" };
const SLOT = { ranking: "Internal ranking", bracket: "Group ranking", members: "Members list" };
const fmt = (v) => (v === null || v === undefined || v === "" ? "—" : Number(v).toLocaleString());
export const parsePower = (s) => {
  const t = String(s ?? "").replace(/[,\s]/g, "").toUpperCase();
  if (!t) return null;
  const m = t.match(/^(\d+(?:\.\d+)?)([KM]?)$/);
  return m ? Math.round(parseFloat(m[1]) * (m[2] === "M" ? 1e6 : m[2] === "K" ? 1e3 : 1)) : NaN;
};
const gname = (g) => (g ? `G${g}` : "Holding Pen");

export const toGroupDraft = (G) => ({
  key: G.finishedAt,
  rows: (G.rows || []).map((r) => ({ ...r, include: r.kind === "match", skipped: false, powerText: r.power ?? "", stats: { ...r.stats } })),
  add: [], bench: [], confirmGroup: false, ignorePhotos: false,
  includeBracket: (G.alliances || []).length > 0, alliances: G.alliances || [],
});

export const groupBlockers = (G, d) => {
  const out = [];
  if (!G || G.status !== "ready" || !d) return ["Not ready"];
  if (G.groupCheck && !d.confirmGroup) out.push(`confirm these are this group's photos`);
  if ((G.photoChecks || []).length && !d.ignorePhotos) out.push("replace the photo in the wrong box, or publish without it");
  const picked = d.rows.filter((r) => r.include && r.matchId);
  const c = {}; picked.forEach((r) => { c[r.matchId] = (c[r.matchId] || 0) + 1; });
  if (Object.values(c).some((n) => n > 1)) out.push("one player is matched to two rows");
  const bad = picked.filter((r) => Number.isNaN(parsePower(r.powerText)));
  if (bad.length) out.push(`fix the power for ${bad.map((r) => r.name).join(", ")}`);
  return out;
};

export const groupPayload = (d) => ({
  rows: d.rows.map((r, i) => ({ r, i })).filter(({ r }) => r.include && r.matchId && r.kind !== "new")
    .map(({ r, i }) => ({ i, matchId: r.matchId, stats: r.inRanking === false ? {} : r.stats, power: parsePower(r.powerText) })),
  add: d.add.filter((i) => !(d.rows[i].include && d.rows[i].matchId)).map((i) => ({ i })),
  bench: d.bench,
  alliances: d.includeBracket ? d.alliances : null,
  confirmGroup: d.confirmGroup, ignorePhotos: d.ignorePhotos,
});

const Photos = ({ G }) => {
  const [big, setBig] = useState(null);
  const ph = G.photos || {};
  const bad = new Set((G.photoChecks || []).map((c) => c.id));
  return (
    <div className="lg-photos" data-testid="group-photos">
      {["ranking", "bracket", "members"].map((k) => (
        <div key={k} className="lg-photoset">
          <div className="lg-photoset-h">{SLOT[k]} · {(ph[k] || []).length}</div>
          <div className="lg-photorow">
            {(ph[k] || []).length === 0 && <span className="lg-was">none sent</span>}
            {(ph[k] || []).map((id, i) => (
              <button key={id} type="button" className={`lg-photo ${bad.has(id) ? "bad" : ""}`} onClick={() => setBig({ id, label: `${SLOT[k]} · photo ${i + 1}` })} aria-label={`Open ${SLOT[k]} photo ${i + 1}`}>
                <img src={fileUrl(`/api/files/id/${id}`)} alt="" /><span>{i + 1}</span>
              </button>
            ))}
          </div>
        </div>
      ))}
      {big && (
        <div className="lg-lightbox" role="dialog" aria-label={big.label} onClick={() => setBig(null)}>
          <div className="lg-lightbox-in" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}><b style={{ marginRight: "auto" }}>{big.label}</b><button className="lg-btn ghost sm" onClick={() => setBig(null)}><X size={14} /></button></div>
            <img src={fileUrl(`/api/files/id/${big.id}`)} alt={big.label} />
          </div>
        </div>
      )}
    </div>
  );
};

export const GroupReview = ({ g, title, G, draft, setDraft, roster, pendingIn, onPublish, onReadAgain, busy }) => {
  const byId = useMemo(() => Object.fromEntries(roster.map((p) => [p.id, p])), [roster]);
  const set = (patch) => setDraft({ ...draft, ...patch });
  const setRow = (i, patch) => set({ rows: draft.rows.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  const gi = Number(g);

  const tableRows = draft.rows.map((r, i) => ({ r, i })).filter(({ r }) => r.kind !== "new");
  // "unsure" rows (a close name is on the roster) appear in the table to match, and here to add if they're new after all
  // CP-009: a "match" row the leader skipped can be added as a new member too
  const newRows = draft.rows.map((r, i) => ({ r, i })).filter(({ r }) => r.kind === "new" || ((r.kind === "unsure" || r.skipped) && !(r.include && r.matchId)));
  const addN = draft.add.filter((i) => !(draft.rows[i]?.include && draft.rows[i]?.matchId)).length;
  const picked = draft.rows.filter((r) => r.include && r.matchId);
  const counts = {}; picked.forEach((r) => { counts[r.matchId] = (counts[r.matchId] || 0) + 1; });
  const kept = new Set(picked.map((r) => r.matchId));
  const missing = roster.filter((p) => Number(p.group) === gi && !kept.has(p.id));
  const moving = picked.filter((r) => byId[r.matchId] && Number(byId[r.matchId].group) !== gi).length;
  const blockers = groupBlockers(G, draft);
  const benchN = draft.bench.filter((id) => missing.some((p) => p.id === id) && !pendingIn[id]).length;

  return (
    <section className="lg-sub" data-testid={`group-review-${g}`}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
        <h4 className="serif" style={{ margin: 0, fontSize: 16 }}>Group {g} · {title}</h4>
        <span style={{ fontSize: 12, color: "var(--muted)" }}>{draft.rows.length} players read · sent by {G.sentBy || "a leader"}{G.hasMembers ? "" : " · no members list, power stays as is"}</span>
      </div>
      <div className="lg-sum">
        <div><b>{picked.length}</b>to update</div><div><b>{moving}</b>moving in</div><div><b>{addN}</b>new to add</div><div><b>{benchN}</b>to Holding Pen</div>
      </div>
      <Photos G={G} />
      {G.simulated && <p className="lg-note">Photo reading isn't available in this view, so these numbers are simulated from the roster.</p>}
      {(G.errors || []).map((e) => <p key={e} className="lg-err" style={{ margin: "6px 0" }}>{e}</p>)}
      {G.groupCheck && (
        <div className={`lg-alert ${draft.confirmGroup ? "warn" : "bad"}`} data-testid={`group-check-${g}`}>
          {draft.confirmGroup
            ? <span>Confirmed: these are Group {g}'s photos. {G.groupCheck.count} players will move in from Group {G.groupCheck.other}.</span>
            : <><span><b>These look like Group {G.groupCheck.other}'s photos.</b> {G.groupCheck.count} of {G.groupCheck.total} names are on {G.groupCheck.other === 0 ? "the Holding Pen" : `Group ${G.groupCheck.other}`}'s roster. Publishing is blocked until the photos are replaced (Season Intake → Change photos) or a leader confirms.</span>
              <div><button className="lg-btn sm" onClick={() => set({ confirmGroup: true })} data-testid={`group-confirm-${g}`}>Yes, these are correct</button></div></>}
        </div>
      )}
      {(G.photoChecks || []).length > 0 && (
        <div className={`lg-alert ${draft.ignorePhotos ? "warn" : "bad"}`} data-testid={`photo-check-${g}`}>
          {G.photoChecks.map((c) => <span key={c.id}><b>{SLOT[c.slot]} photo {c.n}</b> {c.msg}.</span>)}
          <label style={{ display: "flex", gap: 6, alignItems: "center", fontWeight: 600 }}>
            <input type="checkbox" checked={draft.ignorePhotos} onChange={(e) => set({ ignorePhotos: e.target.checked })} data-testid={`photo-ignore-${g}`} />
            Publish without {G.photoChecks.length === 1 ? "this photo" : "these photos"} (or replace in Season Intake → Change photos)
          </label>
        </div>
      )}
      {tableRows.length > 0 && (
        <div className="lg-scroll">
          <table className="lg-itable">
            <thead><tr><th></th><th>Read as</th><th>Roster match</th><th>Power</th>{STATS.map((k) => <th key={k}>{HEAD[k]}</th>)}</tr></thead>
            <tbody>{tableRows.map(({ r, i }) => {
              const cur = byId[r.matchId];
              const id = `group-row-${g}-${i}`;
              const pv = parsePower(r.powerText);
              const pct = cur && cur.power && pv ? (pv - cur.power) / cur.power : null;
              return (
                <tr key={i} className={r.include && r.matchId ? "" : "lg-off"} data-testid={id}>
                  <td><input type="checkbox" checked={r.include && !!r.matchId} disabled={!r.matchId} onChange={(e) => setRow(i, { include: e.target.checked })} data-testid={`${id}-include`} aria-label={`Include ${r.name}`} /></td>
                  <td className="lg-nm">
                    {r.name}
                    {!r.matchId && (r.skipped ? <span className="lg-pill">skipped</span> : <span className="lg-pill warn">no match</span>)}
                    {cur && Number(cur.group) !== gi && <span className="lg-pill info">from {gname(cur.group)}</span>}
                    {r.matchId && counts[r.matchId] > 1 && r.include && <span className="lg-pill warn">matched twice</span>}
                    {G.hasMembers && r.inRanking === false && <span className="lg-pill warn">members list only · stats stay as is</span>}
                    {G.hasMembers && r.inMembers === false && <span className="lg-pill warn">not in members list · may have left</span>}
                  </td>
                  <td>
                    <select className="lg-select lg-mini" value={r.matchId || ""} onChange={(e) => setRow(i, { matchId: e.target.value || null, include: !!e.target.value, skipped: !e.target.value })} data-testid={`${id}-match`}>
                      <option value="">Skip (not updated)</option>
                      {r.suggestions?.length > 0 && <optgroup label="Best guesses">{r.suggestions.map((s) => <option key={"s" + s.id} value={s.id}>{s.name} ({gname(s.group)})</option>)}</optgroup>}
                      <optgroup label="Whole roster">{roster.map((p) => <option key={p.id} value={p.id}>{p.name} ({gname(p.group)})</option>)}</optgroup>
                    </select>
                  </td>
                  <td>
                    <input className="lg-input lg-mini" value={r.powerText} placeholder="stays" onChange={(e) => setRow(i, { powerText: e.target.value })} data-testid={`${id}-power`} />
                    <small className="lg-was">{r.powerRead ? `read "${r.powerRead}" · ` : ""}was {fmt(cur?.power)}{pct !== null && (pct < -0.2 || pct > 0.5) ? " · check" : ""}</small>
                  </td>
                  {STATS.map((k) => (
                    <td key={k}>
                      <input className="lg-input lg-mini" value={r.stats?.[k] ?? ""} disabled={r.inRanking === false} onChange={(e) => setRow(i, { stats: { ...r.stats, [k]: e.target.value === "" ? null : Number(e.target.value.replace(/[^\d.]/g, "")) } })} data-testid={`${id}-${k}`} />
                      <small className="lg-was">was {fmt(r.current?.[k])}</small>
                    </td>
                  ))}
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      )}

      <h5 className="lg-h5">Add to this group <span>· in the photos, not on the roster · tick to add</span></h5>
      {newRows.length === 0 ? <p className="lg-was">Nobody new.</p> : (
        <div className="lg-list">{newRows.map(({ r, i }) => (
          <label key={i} className="lg-li" data-testid={`group-new-${g}-${i}`}>
            <input type="checkbox" checked={draft.add.includes(i)} onChange={(e) => set({ add: e.target.checked ? [...draft.add, i] : draft.add.filter((x) => x !== i) })} />
            <b>{r.name}</b>
            <span className="lg-was">power {r.powerRead || fmt(r.power)} · participation {fmt(r.stats?.participation)} · {fmt(r.stats?.attacks)}/{fmt(r.stats?.defenses)}/{fmt(r.stats?.repairs)}/{fmt(r.stats?.heals)}</span>
            {r.suggestions?.[0] && r.suggestions[0].score >= 0.5 && <span className="lg-was">closest on roster: {r.suggestions[0].name} ({gname(r.suggestions[0].group)})</span>}
          </label>
        ))}</div>
      )}

      <h5 className="lg-h5">Move to Holding Pen <span>· on the roster, not in the photos · tick to move</span></h5>
      {missing.length === 0 ? <p className="lg-was">Everyone on the roster is in the photos.</p> : (
        <div className="lg-list">{missing.map((p) => pendingIn[p.id] ? (
          <div key={p.id} className="lg-li" data-testid={`group-pen-${g}-${p.id}`}>
            <b>{p.name}</b><span className="lg-pill info">found in Group {pendingIn[p.id]}'s photos · moves when Group {pendingIn[p.id]} publishes</span>
          </div>
        ) : (
          <label key={p.id} className="lg-li" data-testid={`group-pen-${g}-${p.id}`}>
            <input type="checkbox" checked={draft.bench.includes(p.id)} onChange={(e) => set({ bench: e.target.checked ? [...draft.bench, p.id] : draft.bench.filter((x) => x !== p.id) })} />
            <b>{p.name}</b><span className="lg-was">power {fmt(p.power)}</span>
            {draft.bench.includes(p.id) ? <span className="lg-pill warn">moves to Holding Pen</span> : <span className="lg-pill">stays in group</span>}
          </label>
        ))}</div>
      )}

      {draft.alliances.length > 0 && <BracketTable g={g} grp={draft} onChange={(next) => set({ includeBracket: next.includeBracket, alliances: next.alliances })} />}

      <div className="lg-gfoot">
        <span style={{ marginRight: "auto", fontSize: 12.5, color: blockers.length ? "var(--danger)" : "var(--muted)", maxWidth: "60ch" }}>
          {blockers.length ? `Can't publish yet: ${blockers.join("; ")}.` : `Publishing updates ${picked.length} players, adds ${addN}, moves ${benchN} to the Holding Pen${draft.includeBracket ? " and replaces the bracket" : ""}. All or nothing, one History entry.`}
        </span>
        <button className="lg-btn ghost sm" onClick={onReadAgain} data-testid={`group-read-again-${g}`}><RefreshCw size={13} />Read again</button>
        <button className="lg-btn" onClick={onPublish} disabled={busy || blockers.length > 0} data-testid={`group-publish-${g}`}>{busy ? "Publishing…" : `Publish Group ${g}`}</button>
      </div>
    </section>
  );
};
